<script setup>
import {ref, computed, watch} from "vue";
import TextInput from "@/Components/TextInput.vue";
import {PhPlusCircle, PhMinusCircle, PhCheckCircle} from '@phosphor-icons/vue';
import Tooltip from "@/Components/Tooltip.vue";

const props = defineProps({
    quantity: Number,
    maxAvailable: Number,
    partialQuantities: Boolean,
});

const emit = defineEmits(['save']);

const input = ref(null);
const inputQuantityStr = ref('' + props.quantity);
const inputQuantity = computed(() => {
    let parsed = parseFloat(inputQuantityStr.value);
    return isNaN(parsed) ? props.quantity : parsed;
});
const dec = computed(() => props.partialQuantities ? 0.25 : 1);
const canDecrease = computed(() => inputQuantity.value - dec.value >= 0.5);
const canIncrease = computed(() => inputQuantity.value < props.maxAvailable);
const decreaseMessage = computed(() => canDecrease.value ? ("Decrease by " + dec.value) : "Minimum quantity");
const increaseMessage = computed(() => canIncrease.value ? "Increase" : "Maximum quantity");

const manualInput = ref(false);
const saving = ref(false);
const unsaved = ref(false);

watch(() => props.quantity, (n) => {
    inputQuantityStr.value = '' + n;
    manualInput.value = false;
    unsaved.value = false;
});

function increase() {
    if (canIncrease.value) {
        let v;
        if (props.partialQuantities && inputQuantity.value % 1 !== 0) {
            v = Math.ceil(inputQuantity.value);
        } else {
            v = inputQuantity.value + 1;
        }
        v = Math.min(v, props.maxAvailable);
        inputQuantityStr.value = '' + v;
        submitValue();
    }
}

function decrease() {
    if (canDecrease.value) {
        inputQuantityStr.value = '' + (inputQuantity.value - dec.value);
        submitValue();
    }
}

function submitValue(blur) {
    let v;
    if (props.partialQuantities) {
        v = Math.round(inputQuantity.value * 4) / 4;
        if (v > 0) {
            v = Math.max(0.5, v);
        }
    } else {
        v = Math.round(inputQuantity.value);
    }
    v = Math.min(v, props.maxAvailable);
    inputQuantityStr.value = '' + v;
    if (v === props.quantity) {
        manualInput.value = false;
        unsaved.value = false;
    } else {
        if (blur) {
            unsaved.value = true;
        } else {
            saving.value = true;
            emit('save', v, () => {
                saving.value = false;
            });
            input.value.blur();
        }
    }
}

function onFocus() {
    manualInput.value = true;
    input.value.select();
}

const buttonTextClass = 'text-gray-600 hover:text-indigo-500';

defineExpose({
    focus: () => input.value.focus(),
    select: () => input.value.select(),
});
</script>

<template>
    <div class="text-center">
        <form @submit.prevent="submitValue(false)">
            <div class="flex-row flex items-center justify-center">
                <Tooltip :message="decreaseMessage" v-if="!manualInput">
                    <button type="button"
                            @click="decrease"
                            :disabled="!canDecrease"
                            class="mr-1 inline-flex h-10 w-10 flex-shrink-0 rounded-full p-1 disabled:opacity-25 hover:bg-gray-200"
                            :class="buttonTextClass">
                        <PhMinusCircle weight="fill" class="w-full h-full" aria-hidden="true"/>
                    </button>
                </Tooltip>
                <template v-else>
                    <div class="mr-1 inline-flex h-10 w-10 flex-shrink-0"></div>
                </template>
                <TextInput
                    ref="input"
                    type="text"
                    inputmode="decimal"
                    aria-label="Quantity"
                    class="text-center block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    v-model="inputQuantityStr"
                    @focus="onFocus"
                    @change="submitValue(true)"
                    @blur="submitValue(true)"
                    :disabled="saving"
                />
                <Tooltip :message="increaseMessage" v-if="!manualInput">
                    <button type="button"
                            @click="increase"
                            :disabled="!canIncrease"
                            class="ml-1 inline-flex h-10 w-10 flex-shrink-0 rounded-full p-1 disabled:opacity-25 hover:bg-gray-200"
                            :class="buttonTextClass">
                        <PhPlusCircle weight="fill" class="w-full h-full" aria-hidden="true"/>
                    </button>
                </Tooltip>
                <Tooltip message="Save changes" v-if="manualInput">
                    <button type="submit"
                            class="ml-1 inline-flex h-10 w-10 flex-shrink-0 rounded-full p-1 disabled:opacity-25 hover:bg-gray-200"
                            :class="buttonTextClass"
                            :disabled="saving">
                        <PhCheckCircle weight="fill" class="w-full h-full" aria-hidden="true"/>
                    </button>
                </Tooltip>
            </div>
        </form>
        <button class="text-sm" v-if="saving">Saving...</button>
        <button class="text-sm" v-else-if="unsaved" @click="submitValue(false)">Unsaved changes!</button>
        <span class="text-sm" v-else-if="manualInput">Enter <template v-if="quantity !== 1">new</template> quantity</span>
        <slot v-else></slot>
    </div>
</template>
